import PropTypes from 'prop-types';
import React from 'react';
import { FormattedMessage } from 'react-intl';
import Immutable from 'immutable';
import ImmutablePropTypes from 'react-immutable-proptypes';
import { bindAll } from '@bibliocommons/utils-react';
import { libraryShape, stackMapShape } from '@bibliocommons/bc-prop-types';
import { PanZoom } from '@bibliocommons/base-pan-zoom';
import { IconButton } from '@bibliocommons/base-buttons';
import { ZoomIn, ZoomOut, Print } from '@bibliocommons/base-icons';
import { createQueryString } from '@bibliocommons/utils-http';
import { openPopUpWindow } from '@bibliocommons/utils-browser';
import { WarningAlert } from '@bibliocommons/alerts';
import Svg from '@bibliocommons/svg';

import './StackMapDisplay.scss';

const MAX_SCALE_FACTOR = 5;
const STACK_MAP_WINDOW_NAME = 'stack-map';

function renderRanges(maps) {
  const ranges = maps.getIn(['ranges', 0]);
  const rangeName = <span className="section">{ranges.get('rangename')}</span>;
  const callNoDisplay = <span className="shelving-range">{ranges.get('callnoDisplay')}</span>;

  return (
    <div className="range-details">
      <FormattedMessage id="stack_map_section" values={{ rangeName }} />,{' '}
      <FormattedMessage id="stack_map_shelving_range" values={{ callNoDisplay }} />
    </div>
  );
}

function renderPlaceHolder() {
  return (
    <Svg className="stack-map-placeholder" width="709" height="96" preserveAspectRatio="none" viewBox="0 0 709 96">
      <path
        fill="currentColor"
        fillRule="evenodd"
        d="M365,84 L365,96 L0,96 L0,84 L365,84 Z M650,60 L650,72 L0,72 L0,60 L650,60 Z M709,36 L709,48 L0,48 L0,36 L709,36 Z M155,0 L155,16 L0,16 L0,0 L155,0 Z"
      />
    </Svg>
  );
}

export default class StackMapDisplay extends React.PureComponent {
  constructor(props) {
    super(props);
    this.state = {
      x: 0,
      y: 0,
      scale: 1
    };
    bindAll(this);
  }

  handleMapUpdate(coordinates) {
    this.setState(coordinates);
  }

  handleZoomIn() {
    this.setState(state => ({
      scale: Math.min(MAX_SCALE_FACTOR, state.scale + 0.5)
    }));
  }

  handleZoomOut() {
    this.setState(state => {
      const newScale = Math.max(1, state.scale - 0.5);
      return {
        scale: newScale,
        x: newScale !== 1 ? state.x : 0,
        y: newScale !== 1 ? state.y : 0
      };
    });
  }

  handlePrint() {
    const stackMap = this.props.stackMap;
    const stackMapSettings = this.props.currentLibrary.get('stackMap');
    const queryParams = createQueryString({
      callno: stackMap.get('callno'),
      library: stackMap.get('library'),
      location: stackMap.get('location'),
      v: 'pf'
    });

    openPopUpWindow({
      url: `https://${stackMapSettings.get('domain')}.stackmap.com/view/?${queryParams}`,
      STACK_MAP_WINDOW_NAME,
      windowFeatures: {
        height: 800,
        width: 950,
        directories: 0
      }
    });
  }

  renderShelfDetails() {
    const { stackMap, currentLibrary } = this.props;
    const maps = stackMap.getIn(['maps', 0], Immutable.Map());
    const settings = currentLibrary.get('stackMap');

    return (
      <div className="library-shelf-details">
        <div className="library-name">{maps.get('library')}</div>
        {maps.get('floorname')}
        {!settings.get('hideRanges') && renderRanges(maps)}
      </div>
    );
  }

  renderMapControls() {
    const zoomIn = (
      <IconButton className="zoom-in" dataKey="zoom-in-button" handleClick={this.handleZoomIn}>
        <ZoomIn />
      </IconButton>
    );

    const zoomOut = (
      <IconButton className="zoom-out" dataKey="zoom-out-button" handleClick={this.handleZoomOut}>
        <ZoomOut />
      </IconButton>
    );

    const print = (
      <IconButton className="print" dataKey="print-button" handleClick={this.handlePrint}>
        <Print />
      </IconButton>
    );

    return (
      <div className="stack-map-controls">
        {zoomIn}
        {zoomOut}
        {print}
      </div>
    );
  }

  renderImage() {
    const maps = this.props.stackMap.getIn(['maps', 0], Immutable.Map());
    return (
      <div className="stack-map-panel" aria-hidden>
        <PanZoom
          className="stack-map-image-wrapper"
          handleUpdate={this.handleMapUpdate}
          {...this.state}
          height={maps.get('height')}
          width={maps.get('width')}
          maxScale={MAX_SCALE_FACTOR}
        >
          <img
            className="stack-map-image"
            data-key="stack-map-image"
            alt={maps.get('library')}
            src={`${maps.get('mapurl')}&marker=1`}
            loading="eager"
          />
        </PanZoom>
      </div>
    );
  }

  renderDirections() {
    const maps = this.props.stackMap.getIn(['maps', 0], Immutable.Map());
    return (
      <div className="stack-map-directions">
        <div className="additional-directions">
          <FormattedMessage id="stack_map_additional_direction" />
        </div>
        {maps.get('directions')}
      </div>
    );
  }

  renderContent() {
    if (this.props.stackMapTransactions.get('noResults') || !this.props.stackMap) {
      return (
        <WarningAlert className="failure-warning">
          <FormattedMessage id="stack_map_failure" />
        </WarningAlert>
      );
    }

    return (
      <div>
        {this.renderShelfDetails()}
        {this.renderMapControls()}
        {this.renderImage()}
        <div className="powered-by">
          <FormattedMessage id="stackmap_powered_by" />
        </div>
        {this.renderDirections()}
      </div>
    );
  }

  render() {
    return (
      <div className="cp-stack-map-display">
        {this.props.stackMapTransactions.get('isFetching') ? renderPlaceHolder() : this.renderContent()}
      </div>
    );
  }
}

StackMapDisplay.propTypes = {
  currentLibrary: libraryShape.isRequired,
  stackMap: stackMapShape,
  stackMapTransactions: ImmutablePropTypes.mapContains({
    open: PropTypes.bool,
    noResults: PropTypes.bool,
    isFetching: PropTypes.bool
  }).isRequired
};
